import React from 'react';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import { withRouter } from 'found';
import withAuth from '../../App/Authenticate';
import {
  Button,
  Divider,
  Dropdown,
  Grid,
  Icon,
  Item,
  Menu
} from 'semantic-ui-react';
import styles from '../../Event/Event.scss';
import MobileHeader from '../../App/MobileHeader';
import TemplateGroup from './TemplateGroup';
import {
  Analytics,
  AvailabilityChecker,
  GlobalNotifications,
  Intent
} from '../../Modules';

const TemplateList = class TemplateList extends React.Component {
  static propTypes = {
    toggleSidebar: PropTypes.func.isRequired,
    sidebarOpen: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'my'
    };
    this.createContractTemplate = this.createContractTemplate.bind(this);
    this.createFormTemplate = this.createFormTemplate.bind(this);
    this.createMpTemplate = this.createMpTemplate.bind(this);
  }

  createFormTemplate() {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create form template',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created form template',
        intent: Intent.SUCCESS
      });
      this.props.router.push(
        `/templates/f/${response.createFormTemplate.form_template.id}/edit`
      );
      Analytics.trackEvent('created-form-template');
    };

    const mutation = graphql`
      mutation TemplateList_CreateFormTemplateMutation(
        $input: CreateFormTemplateInput!
      ) {
        createFormTemplate(input: $input) {
          form_template {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    // TODO: Use RANGE_ADD config

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          title: 'Form Template',
          brand_id: this.props.viewer.primary_brand.id,
          schema: JSON.stringify({
            type: 'object',
            properties: {}
          }),
          ui_schema: JSON.stringify({
            'ui:order': []
          }),
          user_id: this.props.viewer.id
        }
      },
      onCompleted,
      onError
    });
  }

  createContractTemplate() {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create contract template',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created contract template',
        intent: Intent.SUCCESS
      });
      this.props.router.push(
        `/templates/c/${response.createCTemplate.c_template.id}/edit`
      );
      Analytics.trackEvent('created-contract-template');
    };

    const mutation = graphql`
      mutation TemplateList_CreateCTemplateMutation(
        $input: CreateCTemplateInput!
      ) {
        createCTemplate(input: $input) {
          c_template {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          title: 'Contract Template'
        }
      },
      onCompleted,
      onError
    });
  }

  createMpTemplate() {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to create music list template',
        intent: Intent.DANGER
      });
    };

    const onCompleted = response => {
      GlobalNotifications.show({
        message: 'Successfully created music list template',
        intent: Intent.SUCCESS
      });
      this.props.router.push(
        `/templates/m/${response.createMpTemplate.mp_template.id}/edit`
      );
      Analytics.trackEvent('created-mp-template');
    };

    const mutation = graphql`
      mutation TemplateList_CreateMpTemplateMutation(
        $input: CreateMpTemplateInput!
      ) {
        createMpTemplate(input: $input) {
          mp_template {
            id
            title
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          brand_id: this.props.viewer.primary_brand.id,
          user_id: this.props.viewer.id,
          title: 'Music List Template'
        }
      },
      onCompleted,
      onError
    });
  }

  render() {
    const { activeTab } = this.state;
    const { router, sidebarOpen, toggleSidebar, viewer } = this.props;
    let cTemplates = [];
    let formTemplates = [];
    let mpTemplates = [];
    if (activeTab === 'my') {
      cTemplates = viewer.c_templates.edges;
      formTemplates = viewer.form_templates.edges;
      mpTemplates = viewer.mp_templates.edges;
    } else {
      mpTemplates = viewer.public_mp_templates.edges;
    }
    const hasPublicTemplates =
      viewer.public_mp_templates.edges.length > 0;
    const hasTemplates =
      cTemplates.length > 0 ||
      formTemplates.length > 0 ||
      mpTemplates.length > 0;

    const NewTemplate = ({ text, primary }) =>
      <Dropdown
        text={text}
        floating
        labeled
        button
        className={`icon ${primary ? 'primary' : 'basic'}`}
      >
        <Dropdown.Menu className={styles.menuRight}>
          <Dropdown.Item
            onClick={this.createContractTemplate}
            text='Contract Template'
          />
          <Dropdown.Item
            onClick={this.createFormTemplate}
            text='Form Template'
          />
          {viewer.features.music_lists &&
            <Dropdown.Item
              onClick={this.createMpTemplate}
              text='Music List Template'
            />}
        </Dropdown.Menu>
      </Dropdown>;

    return (
      <div>
        <MobileHeader
          viewer={viewer}
          router={router}
        >
          <div className='menu'>
            <Item
              as='a'
              className={sidebarOpen ? 'active ' : ''}
              onClick={toggleSidebar}
            >
              <Icon name='sidebar' />
            </Item>
          </div>
          <div className='right menu'>
            <AvailabilityChecker viewer={viewer} mobile />
            <Item as='a' onClick={router.push.bind(null, '/search')}>
              <Icon name='search' />
            </Item>
          </div>
        </MobileHeader>
        <Grid container stackable>
          <Grid.Row rows='equal' style={{ marginTop: '2em' }}>
            <Grid.Column>
              <Menu secondary pointing>
                <Menu.Item
                  name='My Templates'
                  active={activeTab === 'my'}
                  onClick={() => this.setState({ activeTab: 'my' })}
                />
                {hasPublicTemplates &&
                  <Menu.Item
                    name='Discover'
                    active={activeTab === 'discover'}
                    onClick={() => this.setState({ activeTab: 'discover' })}
                  />}
              </Menu>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              {hasTemplates &&
                <Grid.Row style={{ margin: '1em 0' }}>
                  <Grid.Column align='right'>
                    <NewTemplate text='New' primary />
                  </Grid.Column>
                </Grid.Row>}
              <TemplateGroup
                templates={cTemplates}
                documentType='Contract'
                color='green'
                documentPath='/templates/c/'
              />
              <TemplateGroup
                templates={formTemplates}
                documentType='Form'
                color='yellow'
                documentPath='/templates/f/'
              />
              <TemplateGroup
                templates={mpTemplates}
                documentType='Music List'
                color='purple'
                documentPath='/templates/m/'
              />
              {!hasTemplates &&
                <div>
                  <h3 style={{ textAlign: 'center', margin: '2em' }}>
                    You have no saved templates
                  </h3>
                  <p
                    style={{
                      textAlign: 'center',
                      maxWidth: '400px',
                      margin: '0 auto'
                    }}
                  >
                    {hasPublicTemplates &&
                      <React.Fragment>
                        <Button
                          icon='search'
                          labelPosition='left'
                          content='Browse Cloverbook Templates'
                          primary
                          onClick={() =>
                            this.setState({ activeTab: 'discover' })}
                        />
                        <Divider horizontal>Or</Divider>
                      </React.Fragment>}
                    <NewTemplate text='Create New' />
                  </p>
                </div>}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }
};

export default withRouter(withAuth(TemplateList));



// WEBPACK FOOTER //
// ./webpack/components/Document/Template/TemplateList.js